import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/api';

interface StockAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  companyName?: string;
  stockData: Record<string, any>;
}

const StockAnalysisModal: React.FC<StockAnalysisModalProps> = ({
  isOpen,
  onClose,
  symbol,
  companyName,
  stockData,
}) => {
  const [text, setText] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textRef = useRef('');
  const containerRef = useRef<HTMLDivElement>(null);

  const startAnalysis = useCallback(() => {
    setText('');
    textRef.current = '';
    setError(null);
    setStreaming(true);

    apiService.streamStockAnalysis(
      symbol,
      stockData,
      (chunk) => {
        textRef.current += chunk;
        setText(textRef.current);
      },
      () => setStreaming(false),
      (err) => {
        setError(err);
        setStreaming(false);
      },
    );
  }, [symbol, stockData]);

  useEffect(() => {
    if (isOpen) startAnalysis();
  }, [isOpen, startAnalysis]);

  // Auto-scroll as text streams in
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [text]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '90%', maxWidth: 620,
        maxHeight: '80vh',
        background: '#0f1419', border: '1px solid #2a3142',
        borderRadius: 12, display: 'flex', flexDirection: 'column',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderBottom: '1px solid #1f2937', flexShrink: 0,
        }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb' }}>{symbol}</span>
            {companyName && (
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>{companyName}</span>
            )}
            <span style={{ fontSize: 11, color: '#4b5563', marginLeft: 10 }}>AI Analysis</span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: '#6b7280',
              cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 6px',
            }}
          >
            x
          </button>
        </div>

        {/* Body */}
        <div
          ref={containerRef}
          style={{
            flex: 1, overflowY: 'auto', padding: '16px 18px',
            fontSize: 13, lineHeight: 1.7, color: '#d1d5db',
            whiteSpace: 'pre-wrap',
          }}
        >
          {error ? (
            <div style={{ color: '#ef4444' }}>Error: {error}</div>
          ) : text ? (
            text
          ) : streaming ? (
            <span style={{ color: '#6b7280' }}>Analyzing {symbol}...</span>
          ) : null}
          {streaming && <span style={{ color: '#3b82f6', animation: 'pulse 1.5s infinite' }}>|</span>}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default StockAnalysisModal;
